/**
 * How long a cart may sit in a stage before it counts as stalling.
 *
 * Previously only CART and REQUESTED were aged, which meant the "abandoned" figure
 * could only ever inspect a handful of carts while the two stages holding the most
 * value — QUOTE and HOLD — were never checked at all. A quote issued eight months
 * ago and never answered looked identical to one sent this morning.
 *
 * HOLD gets a longer fuse than the rest: a hold is a deliberate pause, so it is
 * expected to last, but it also silences follow-up reminders and nothing else ever
 * brings it back. A month is the point at which "paused" has become "forgotten".
 *
 * Stages absent from this map are terminal (CONVERTED, CLOSED_LOST) or not yet real
 * (DRAFT) and are never aged.
 */
export const STALE_AFTER_DAYS = {
    REQUESTED: 14,
    CART: 14,
    QUOTE: 14,
    HOLD: 30,
};

/** Stages that are aged at all, in pipeline order. */
export const AGED_STATUSES = ['REQUESTED', 'CART', 'QUOTE', 'HOLD'];

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * How long this cart has been sitting in its current stage.
 *
 * Measured from statusChangedAt — the moment it entered the stage — rather than
 * updatedAt, which any ordinary edit bumps and which would therefore reset the
 * clock every time someone opened the cart and changed a note. Carts that predate
 * that column fall back to updatedAt and are flagged inexact rather than dropped,
 * since excluding them would under-report the stall that is actually there.
 *
 * Returns null when a cart carries no usable timestamp at all.
 */
export const ageInStage = (cart, now = Date.now()) => {
    const source = cart.statusChangedAt || cart.updatedAt || cart.createdAt;
    if (!source) return null;
    const ms = now - new Date(source).getTime();
    if (Number.isNaN(ms)) return null;
    return { days: Math.max(0, ms / DAY_MS), exact: Boolean(cart.statusChangedAt) };
};

/** Is this cart past the threshold for the stage it is in? */
export const isStalling = (cart, now = Date.now()) => {
    const threshold = STALE_AFTER_DAYS[cart.status];
    if (threshold == null) return false;
    const age = ageInStage(cart, now);
    return age != null && age.days > threshold;
};

/** Whole days, for display. */
export const stageDays = (cart, now = Date.now()) => {
    const age = ageInStage(cart, now);
    return age == null ? null : Math.floor(age.days);
};
